/**
 * RTK Query slice for Courier inbox token + user preferences.
 *
 * Token is short-lived (~5 min) and minted by the BFF for the signed-in
 * user. Preferences read/write go through the BFF, which merges Courier
 * topics with the Supabase-owned per-channel enabled set.
 */

import { createApi } from '@reduxjs/toolkit/query/react';
import type {
  CourierUserPreferencesChannel,
  CourierUserPreferencesStatus,
} from '@trycourier/courier-js';

import {
  fetchCourierInboxToken,
  setNotificationPreferenceToggle,
  fetchNotificationPreferences,
} from '../util/api';
import * as log from '../util/log';

export type CourierInboxToken = {
  token: string;
  userId: string;
};

export type NotificationPreferenceTopic = {
  topicId: string;
  topicName: string;
  sectionId: string;
  sectionName: string;
  status: CourierUserPreferencesStatus;
  defaultStatus: CourierUserPreferencesStatus;
  hasCustomRouting: boolean;
  customRouting: CourierUserPreferencesChannel[];
};

export type SetNotificationPreferenceToggleArgs = {
  topicId: string;
  channel: Channel;
  enabled: boolean;
  consentTextVersion?: string;
};

/** Delivery channels a topic can route to, in display/priority order. */
export const ALL_CHANNELS = ['email', 'in-app', 'sms', 'push'] as const;
export type Channel = typeof ALL_CHANNELS[number];

const MARKETING_TOPIC_NAME = 'Marketing Notifications';

const REQUIRED_EMAIL_TOPICS = ['Account Notifications', 'Buying and Selling'];

/** Channels permanently disabled for a topic. Marketing never goes via SMS. */
export const bannedChannelsFor = (topicName: string): readonly Channel[] =>
  topicName === MARKETING_TOPIC_NAME ? ['sms'] : [];

/** Whether email is mandatory (locked on, cannot be turned off) for a topic. */
export const isEmailRequired = (topicName: string): boolean =>
  REQUIRED_EMAIL_TOPICS.includes(topicName);

/** Whether a channel is currently enabled in a topic's routing. */
export const isChannelEnabledInPref = (
  pref: NotificationPreferenceTopic,
  channel: Channel
): boolean => {
  if (pref.status === 'OPTED_OUT') return false;
  if (channel === 'sms') return pref.hasCustomRouting && pref.customRouting.includes('sms');
  if (!pref.hasCustomRouting) return true;
  return pref.customRouting.includes(channel);
};

/** Whether a channel reads as ON in the UI. Push also needs a live subscription. */
export const isChannelOn = (
  pref: NotificationPreferenceTopic,
  channel: Channel,
  pushSubscribed: boolean
): boolean => {
  const enabled = isChannelEnabledInPref(pref, channel);
  if (channel === 'push') return enabled && pushSubscribed;
  return enabled;
};

const SLICE_NAME = 'notificationsApi';

export const notificationsApi = createApi({
  reducerPath: SLICE_NAME,
  baseQuery: () => ({ data: null }),
  tagTypes: ['CourierToken', 'CourierPreferences'],
  keepUnusedDataFor: 240,
  endpoints: build => ({
    fetchCourierInboxToken: build.query<CourierInboxToken, void>({
      providesTags: ['CourierToken'],
      async queryFn() {
        try {
          const data = (await fetchCourierInboxToken()) as CourierInboxToken;
          return { data };
        } catch (e) {
          log.error(
            e instanceof Error ? e : new Error(String(e)),
            'notifications.courier_token.fetch_failed',
            {}
          );
          return {
            error: {
              status: (e as { status?: number })?.status || 500,
              data: { code: 'COURIER_TOKEN_FAILED' },
            },
          };
        }
      },
    }),
    getNotificationPreferences: build.query<NotificationPreferenceTopic[], void>({
      providesTags: ['CourierPreferences'],
      async queryFn() {
        try {
          const result = (await fetchNotificationPreferences()) as {
            topics?: NotificationPreferenceTopic[];
          };
          return { data: result.topics ?? [] };
        } catch (e) {
          log.error(
            e instanceof Error ? e : new Error(String(e)),
            'notifications.preferences.fetch_failed',
            {}
          );
          return {
            error: {
              status: 500,
              data: { code: 'COURIER_PREFERENCES_FETCH_FAILED' },
            },
          };
        }
      },
    }),
    setNotificationPreferenceTopic: build.mutation<
      NotificationPreferenceTopic,
      SetNotificationPreferenceToggleArgs
    >({
      async queryFn(args) {
        try {
          const data = (await setNotificationPreferenceToggle(args)) as NotificationPreferenceTopic;
          return { data };
        } catch (e) {
          log.error(
            e instanceof Error ? e : new Error(String(e)),
            'notifications.preferences.update_failed',
            { topicId: args.topicId }
          );
          return {
            error: {
              status: (e as { status?: number })?.status || 500,
              data: { code: 'COURIER_PREFERENCES_UPDATE_FAILED' },
            },
          };
        }
      },
      async onQueryStarted(_args, { dispatch, queryFulfilled }) {
        try {
          const { data: updated } = await queryFulfilled;
          dispatch(
            notificationsApi.util.updateQueryData(
              'getNotificationPreferences',
              undefined,
              draft => {
                const i = draft.findIndex(p => p.topicId === updated.topicId);
                if (i >= 0) {
                  // Courier's per-user topic API omits section metadata; keep the cached section.
                  draft[i] = {
                    ...updated,
                    sectionId: draft[i].sectionId,
                    sectionName: draft[i].sectionName,
                  };
                }
              }
            )
          );
        } catch {
          // BFF rejected the toggle; getNotificationPreferences cache is left untouched.
        }
      },
    }),
  }),
});

export const {
  useFetchCourierInboxTokenQuery,
  useGetNotificationPreferencesQuery,
  useSetNotificationPreferenceTopicMutation,
} = notificationsApi;
export default notificationsApi.reducer;
